import {
	type GraphIssue,
	NODE_TYPE_LABELS,
	type NodeType,
	summarizeNode,
} from "@velizhanin-dev/bot-scenario"
import { AlertTriangle, CheckCircle2, CircleAlert } from "lucide-react"
import { useState } from "react"
import { plural } from "@/entities/audience"
import type { ScenarioDraft } from "@/entities/bot-builder"
import type { GraphIssues } from "@/features/edit-scenario-draft"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover"

/** Ошибки и предупреждения проверки графа со ссылкой на блок. */
export function IssuesMenu({
	draft,
	graphIssues,
	onPickNode,
	onPickFlow,
}: {
	draft: ScenarioDraft
	graphIssues: GraphIssues
	onPickNode: (nodeId: string) => void
	onPickFlow: (flowId: string) => void
}) {
	const [open, setOpen] = useState(false)
	const { issues, errorCount, warningCount } = graphIssues
	const sorted = [...issues].sort((a, b) =>
		a.severity === b.severity ? 0 : a.severity === "error" ? -1 : 1,
	)
	const nodes = new Map(draft.nodes.map((node) => [node.id, node]))
	const flows = new Map(draft.flows.map((flow) => [flow.id, flow]))

	const describe = (issue: GraphIssue) => {
		const node = issue.nodeId ? nodes.get(issue.nodeId) : undefined
		if (node) {
			return `${flows.get(node.flowId)?.name ?? ""} · ${NODE_TYPE_LABELS[node.type as NodeType] ?? node.type}: ${summarizeNode(node, draft.flows, draft.tags)}`
		}
		return issue.flowId ? (flows.get(issue.flowId)?.name ?? "") : "Сценарий"
	}

	const label =
		errorCount > 0
			? `${errorCount} ${plural(errorCount, ["ошибка", "ошибки", "ошибок"])}`
			: warningCount > 0
				? `${warningCount} ${plural(warningCount, ["замечание", "замечания", "замечаний"])}`
				: "Без ошибок"

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="ghost"
					aria-label={`Проблемы сценария: ${label}`}
					title={label}
					className={cn(
						"h-9 min-w-[3.5rem] shrink-0 justify-center gap-1.5 rounded-xl px-2.5 py-0 tabular-nums text-xs font-medium whitespace-nowrap [&_svg]:size-4",
						errorCount > 0
							? "bg-red-500/15 text-red-300 hover:bg-red-500/25"
							: warningCount > 0
								? "bg-amber-400/10 text-amber-300 hover:bg-amber-400/20"
								: "text-emerald-300/90 hover:bg-accent",
					)}
				>
					{errorCount > 0 ? (
						<CircleAlert />
					) : warningCount > 0 ? (
						<AlertTriangle />
					) : (
						<CheckCircle2 />
					)}
					<span className="hidden md:inline">
						{errorCount > 0 ? errorCount : warningCount > 0 ? warningCount : "Без ошибок"}
					</span>
				</Button>
			</PopoverTrigger>
			<PopoverContent align="end" className="max-h-96 w-96 overflow-y-auto rounded-2xl p-1">
				{sorted.length === 0 ? (
					<p className="p-4 text-center text-sm text-muted-foreground">Проверка не нашла проблем</p>
				) : null}
				{sorted.map((issue, index) => (
					<Button
						key={`${issue.code}-${issue.nodeId ?? issue.flowId ?? ""}-${index}`}
						type="button"
						variant="ghost"
						className="h-auto w-full items-start justify-start gap-2.5 rounded-xl px-2.5 py-2 text-left"
						onClick={() => {
							setOpen(false)
							if (issue.nodeId && nodes.has(issue.nodeId)) onPickNode(issue.nodeId)
							else if (issue.flowId) onPickFlow(issue.flowId)
						}}
					>
						{issue.severity === "error" ? (
							<CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
						) : (
							<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
						)}
						<span className="min-w-0">
							<span className="block text-sm">{issue.message}</span>
							<span className="block truncate text-xs text-muted-foreground">
								{describe(issue)}
							</span>
						</span>
					</Button>
				))}
			</PopoverContent>
		</Popover>
	)
}
